import React, { Component } from 'react';
import loadable from '@loadable/component';
import { connect } from 'react-redux';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import find from 'ramda/src/find';
import isEmpty from 'ramda/src/isEmpty';
import isNil from 'ramda/src/isNil';
import propEq from 'ramda/src/propEq';
import Avatar from '@components/@core/Avatar';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Filter from '@components/@core/Filter';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import AppActions from '~/actions/app';
import DevelopActions from '~/actions/develop';
import ManageActions from '~/actions/manage';
import { IndividualTrainingTabs } from '~/services/config';
import withNavigate from '~/hocs/withNavigate';
import Report from './Report';

const PdfIcon = loadable(() => import('./IndividualTrainingPdf'), { ssr: false });
const moment = extendMoment(originalMoment);

class IndividualTraining extends Component {
    constructor(props) {
        super(props);

        const userId = Number(props.match?.params?.userId || 0);
        const companyId = Number(props.match?.params?.companyId || 0);
        const dateStart = moment().startOf('month').format('YYYY-MM-DD');
        const dateEnd = moment().endOf('month').format('YYYY-MM-DD');
        const company = { id: companyId };
        const user = find(propEq('id', userId), props.employees) || props.location.state?.user || { id: userId };

        this.state = {
            dateStart,
            dateEnd,
            user,
            company,
            userReport: {},
            activeTab: 'manager',
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const { trainingReports } = nextProps;
        const { user } = prevState;
        if (isEmpty(trainingReports.individuals) || isNil(trainingReports.individuals)) {
            return { userReport: {} };
        }
        const userReport = find(propEq('id', user.id), trainingReports.individuals) || {};
        return { userReport };
    }

    componentDidMount() {
        const { company, user, dateStart, dateEnd } = this.state;
        this.fetchDetails(company, user, dateStart, dateEnd);
    }

    handleFilter = (type, data) => {
        const { company, user, dateStart, dateEnd } = this.state;
        if (type === 'employee') {
            const user = data[0].id < 0 ? this.state.user : data[0];
            if (this.state.user.id !== user.id) {
                this.fetchDetails(company, user, dateStart, dateEnd);
                this.props.navigate(`/hcm/report-training/${company.id}/${data[0].id}`);
            }
            this.setState({ user });
        } else if (type === 'company') {
            this.setState({ company: data[0] });
            if (company.id !== data[0].id) {
                this.fetchDetails(data[0], user, dateStart, dateEnd);
                this.props.navigate(`/hcm/report-training/${data[0].id}/${user.id}`);
            }
        }
    };

    handleDateChange = (e) => {
        const { company, user } = this.state;
        const dateStart = moment(e.start).format('YYYY-MM-DD');
        const dateEnd = moment(e.end).format('YYYY-MM-DD');
        this.fetchDetails(company, user, dateStart, dateEnd);
        this.setState({ dateStart, dateEnd });
    };

    fetchDetails = (company, user, dateStart, dateEnd) => {
        this.props.getUserCourseReport(user.id, dateStart, dateEnd);
        const type = 'individual';
        const payload = {
            page: 1,
            per_page: 500,
            date_start: dateStart,
            date_end: dateEnd,
            company_id: [company.id],
        };
        this.props.getReports(payload, type);
    };

    handleSelectMenu = (card, event) => {
        const { user } = this.state;
        if (event === 'edit') {
            this.props.toggleModal({
                type: 'Quick Edit',
                data: {
                    before: {
                        template: card,
                        type: 'courses',
                        from: 'instance',
                        userId: user.id,
                    },
                },
                callBack: {
                    onDelete: () => this.handleDeleteCourse(card),
                },
            });
        } else if (event === 'unassign') {
            this.handleDeleteCourse(card);
        }
    };

    handleDeleteCourse = (course) => {
        const { user, dateStart, dateEnd } = this.state;
        this.props.deleteCard({
            event: 'delete',
            cardId: course.id,
            card: course,
            after: {
                type: 'USERSCOURSEREPORT_REQUEST',
                userId: user.id,
                dateStart,
                dateEnd,
            },
        });
    };

    render() {
        const { selfsCoursesReport, careerCoursesReport, managerCoursesReport, role } = this.props;
        const { user, userReport, dateStart, dateEnd, activeTab } = this.state;

        // Show loading state instead of returning null
        if (isEmpty(userReport)) {
            return (
                <ErrorBoundary className="individual-reports">
                    <Filter multi={[false, false]} back="all trainings" onChange={this.handleFilter} />
                    <div className="card">
                        <div className="d-flex justify-content-between">
                            <span className="dsl-b22 bold">Individual Training Report</span>
                        </div>
                        <div className="mt-4 text-center">
                            <p className="dsl-m14">Loading training report data...</p>
                            <p className="dsl-m12 text-400">
                                User ID: {user?.id}, Company ID: {this.state.company?.id}
                            </p>
                        </div>
                    </div>
                </ErrorBoundary>
            );
        }

        const { manager, career, self, overall } = userReport.report;
        const date = moment.range(dateStart, dateEnd);

        return (
            <ErrorBoundary className="individual-reports">
                <Filter multi={[false, false]} back="all trainings" onChange={this.handleFilter} />
                <div className="card">
                    <div className="d-flex justify-content-between">
                        <span className="dsl-b22 bold">Individual Training Report</span>
                        <div className="d-h-end">
                            <DatePicker calendar="range" append="caret" format="MMM D" as="span" align="right" value={date} onSelect={this.handleDateChange} />
                            <div className="d-h-end cursor-pointer ms-3">
                                <PdfIcon state={this.state} props={this.props} />
                            </div>
                        </div>
                    </div>
                    <div className="d-flex ssm-column mt-4 px-0 px-sm-5">
                        <div className="d-flex-1">
                            <Avatar className="mx-auto" name={user.name} url={user.profile.avatar} size="medium" />
                            <p className="dsl-b16 mb-0 mt-3 text-400 text-center">{user.name}</p>
                        </div>
                        <div className="d-flex-1" />
                        <div className="d-flex-4">
                            <div className="d-flex my-2">
                                <div className="d-flex-2" />
                                <div className="d-flex-2 text-end dsl-m12">Assigned</div>
                                <div className="d-flex-1 text-end dsl-m12">Open</div>
                                <div className="d-flex-1 text-end dsl-m12 me-0 ssm-r-1">Past Due</div>
                                <div className="d-flex-1 text-end dsl-m12">Complete</div>
                            </div>
                            <div className="d-flex my-2">
                                <div className="d-flex-2 text-end dsl-m12">Manager Assigned</div>
                                <div className="d-flex-2 text-end dsl-m12">{manager.courses_assigned.count}</div>
                                <div className="d-flex-1 text-end dsl-m12">{manager.courses_incompleted.count}</div>
                                <div className="d-flex-1 text-end dsl-m12">{manager.courses_past_due.count}</div>
                                <div className="d-flex-1 text-end dsl-m12">{manager.courses_completed.count}</div>
                            </div>
                            <div className="d-flex my-2">
                                <div className="d-flex-2 text-end dsl-m12">Health Plan Assigned</div>
                                <div className="d-flex-2 text-end dsl-m12">{career.courses_assigned.count}</div>
                                <div className="d-flex-1 text-end dsl-m12">{career.courses_incompleted.count}</div>
                                <div className="d-flex-1 text-end dsl-m12">{career.courses_past_due.count}</div>
                                <div className="d-flex-1 text-end dsl-m12">{career.courses_completed.count}</div>
                            </div>
                            <div className="d-flex my-2">
                                <div className="d-flex-2 text-end dsl-m12">Self Assigned</div>
                                <div className="d-flex-2 text-end dsl-m12">{self.courses_assigned.count}</div>
                                <div className="d-flex-1 text-end dsl-m12">{self.courses_incompleted.count}</div>
                                <div className="d-flex-1 text-end dsl-m12">{self.courses_past_due.count}</div>
                                <div className="d-flex-1 text-end dsl-m12">{self.courses_completed.count}</div>
                            </div>
                            <div className="d-flex my-2">
                                <div className="d-flex-2 text-end dsl-b12 bold">Total Assigned</div>
                                <div className="d-flex-2 text-end dsl-b12 bold">{overall.courses_assigned.count}</div>
                                <div className="d-flex-1 text-end dsl-b12 bold">{overall.courses_incompleted.count}</div>
                                <div className="d-flex-1 text-end dsl-b12 bold">{overall.courses_past_due.count}</div>
                                <div className="d-flex-1 text-end dsl-b12 bold">{overall.courses_completed.count}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card mt-3">
                    <p className="dsl-b22 bold">Courses Assigned</p>
                    <ResponsiveTabs
                        className="ind-account-admin-content"
                        data={IndividualTrainingTabs}
                        active={activeTab}
                        getId={(e) => e['name']}
                        getValue={(e) => e['label']}
                        onTab={(e) => this.setState({ activeTab: e.name })}
                    />
                    <Tab eventKey="manager" title="Manager Assigned" active={activeTab}>
                        <Report type="manager" data={managerCoursesReport} userRole={role} onSelectMenu={this.handleSelectMenu} />
                    </Tab>
                    <Tab eventKey="career" title="Career Assigned" active={activeTab}>
                        <Report type="career" data={careerCoursesReport} userRole={role} onSelectMenu={this.handleSelectMenu} />
                    </Tab>
                    <Tab eventKey="self" title="Self Assigned" active={activeTab}>
                        <Report type="self" data={selfsCoursesReport} userRole={role} onSelectMenu={this.handleSelectMenu} />
                    </Tab>
                </div>
            </ErrorBoundary>
        );
    }
}

const mapStateToProps = (state) => ({
    role: state.app.app_role_id,
    employees: state.app.users,
    trainingReports: state.manage.trainingReports.individual,
    selfsCoursesReport: state.develop.selfsCoursesReport,
    careerCoursesReport: state.develop.careerCoursesReport,
    managerCoursesReport: state.develop.managerCoursesReport,
});

const mapDispatchToProps = (dispatch) => ({
    getReports: (payload, type) => dispatch(ManageActions.posttrainingreportsRequest(payload, type)),
    getUserCourseReport: (userId, dateStart, dateEnd) => dispatch(DevelopActions.userscoursereportRequest(userId, dateStart, dateEnd)),
    deleteCard: (e) => dispatch(DevelopActions.updatecardinstanceRequest(e)),
    toggleModal: (e) => dispatch(AppActions.modalRequest(e)),
});

const IndividualTrainingWithNavigate = withNavigate(IndividualTraining);

export default connect(mapStateToProps, mapDispatchToProps)(IndividualTrainingWithNavigate);
